/**
 * Универсальный компонент Dropdown (выпадающее меню) с поддержкой compound pattern.
 *
 * Позволяет создавать выпадающие меню с любым содержимым, включая дефолтные элементы Dropdown.List и Dropdown.ListItem,
 * либо кастомные компоненты. Автоматически закрывается по клику вне области меню или по клику вне кнопки-открывашки.
 *
 * @component
 * @example
 * // Дефолтный вариант с List и ListItem:
 * <Dropdown isOpen={isOpen} onClose={handleClose} buttonRef={buttonRef}>
 *   <Dropdown.List>
 *     <Dropdown.ListItem>Пункт 1</Dropdown.ListItem>
 *     <Dropdown.ListItem>Пункт 2</Dropdown.ListItem>
 *   </Dropdown.List>
 * </Dropdown>
 *
 * // Кастомный контент:
 * <Dropdown isOpen={isOpen} onClose={handleClose} buttonRef={buttonRef}>
 *   <div>Любой JSX</div>
 * </Dropdown>
 *
 * @param {boolean} isOpen — Управляет видимостью выпадающего меню.
 * @param {() => void} onClose — Функция, вызываемая для закрытия меню (например, при клике вне области меню).
 * @param {React.RefObject<HTMLElement>} [buttonRef] — ref на кнопку/элемент-открывашку, чтобы игнорировать клик по ней.
 * @param {string} [className] — Дополнительные CSS-классы для контейнера выпадающего меню.
 * @param {React.ReactNode} children — Содержимое дропдауна. Можно использовать как Dropdown.List/Dropdown.ListItem, так и любой кастомный JSX.
 *
 * @property {React.FC<{ className?: string }>} List — Дефолтный компонент-обёртка для списка пунктов меню.
 * @property {React.FC<{ className?: string }>} ListItem — Дефолтный компонент элемента меню.
 *
 * @returns {JSX.Element|null} Выпадающее меню, если isOpen === true; иначе — null.
 */

import React, { useEffect, useRef, PropsWithChildren } from 'react';

import cn from 'classnames';

import cls from './Dropdown.module.css';

interface DropdownProps {
    isOpen: boolean;
    onClose: () => void;
    className?: string;
    children: React.ReactNode;
    buttonRef?: React.RefObject<HTMLElement>;
}

const List = ({ children, className }: PropsWithChildren<{ className?: string }>) => (
    <ul className={cn(cls.dropdownList, className)}>{children}</ul>
);

const ListItem = ({
    children,
    className,
    ...rest
}: PropsWithChildren<{ className?: string; onClick?: () => void }>) => (
    <li className={cn(cls.dropdownListItem, className)} {...rest}>
        {children}
    </li>
);

type DropdownCompoundComponent = React.FC<DropdownProps> & {
    List: typeof List;
    ListItem: typeof ListItem;
};

const DropdownComponent: DropdownCompoundComponent = ({
    isOpen,
    onClose,
    children,
    buttonRef,
    className,
}) => {
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(target) &&
                (!buttonRef?.current || !buttonRef.current.contains(target))
            ) {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose, buttonRef]);

    if (!isOpen) return null;

    return (
        <div ref={dropdownRef} className={cn(cls.dropdownContainer, className)}>
            {children}
        </div>
    );
};

DropdownComponent.List = List;
DropdownComponent.ListItem = ListItem;

export { DropdownComponent as Dropdown };
